import { toPositiveOptionId } from '@/utils/optionValues';
import { parseAmountInput } from '@/utils/numberValue';
import { formatDateForApi } from '@/utils/datePresentation';
import {
  CORE_STATUS_OPTIONS,
  CORE_FREQUENCY_OPTIONS,
} from '@/composables/shared/useCoreOptionSets';
import { TAX_MODE_OPTIONS } from '@/composables/shared/useTaxModeOptions';

export const EXPENSE_FEE_STATUS_OPTIONS = CORE_STATUS_OPTIONS;

export const EXPENSE_FEE_FREQUENCY_OPTIONS = CORE_FREQUENCY_OPTIONS;

export const EXPENSE_FEE_TAX_MODE_OPTIONS = TAX_MODE_OPTIONS;

export const EXPENSE_FEE_LOCAL_TAX_TYPE_OPTIONS = [
  { value: 'trasladado', label: 'Trasladado' },
  { value: 'retenido', label: 'Retenido' },
];

export const parseExpenseFeeNumericInput = (value) => parseAmountInput(value, 0);

export const formatExpenseFeeDateForApi = (value) => {
  return formatDateForApi(value, { emptyValue: '', allowString: true });
};

export const buildTodayDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

export const createInitialExpenseFeeTaxPreview = () => ({
  amount: 0,
  discount: 0,
  subtotal: 0,
  calculation_type: 'from-total',
  tax_type: 'individual',
  taxes: {
    transferred_total: 0,
    withheld_total: 0,
    local_transferred_total: 0,
    local_withheld_total: 0,
    net_total: 0,
    items: [],
    summary: [],
  },
  total: 0,
});

export const normalizeExpenseFeeLocalTaxes = (localTaxes) => {
  return (Array.isArray(localTaxes) ? localTaxes : [])
    .map((item) => ({
      type: String(item?.type || 'trasladado').trim().toLowerCase(),
      name: String(item?.name || '').trim(),
      percentage: parseExpenseFeeNumericInput(item?.percentage),
      amount: parseExpenseFeeNumericInput(item?.amount),
    }));
};

export const buildExpenseFeeTagsPayload = (rawTags) => {
  const selectedTags = Array.isArray(rawTags) ? rawTags : [];
  const tagIds = selectedTags
    .map((item) => toPositiveOptionId(item))
    .filter((id) => id > 0);
  const tagNames = selectedTags
    .map((item) => {
      if (typeof item === 'string') {
        return item.trim();
      }

      return String(item?.label || item?.name || '').trim();
    })
    .filter((value) => value !== '');

  return {
    tagIds,
    tagNames,
  };
};

export const buildExpenseFeeTaxPreviewPayload = (form = {}) => {
  const withoutTaxes = Boolean(form?.withoutTaxes);
  const taxMode = String(form?.taxType || 'individual').trim().toLowerCase() === 'group'
    ? 'group'
    : 'individual';

  const taxIds = (Array.isArray(form?.taxes) ? form.taxes : [])
    .map((item) => toPositiveOptionId(item))
    .filter((id) => id > 0);

  const taxGroupId = toPositiveOptionId(form?.taxGroup);

  const localTaxes = normalizeExpenseFeeLocalTaxes(form?.localTaxes)
    .filter((item) => item.name !== '' || item.amount > 0 || item.percentage > 0);

  const discount = parseExpenseFeeNumericInput(form?.discount);
  const deductions = parseExpenseFeeNumericInput(form?.deductions);

  return {
    amount: parseExpenseFeeNumericInput(form?.amount),
    // Deducciones behaves like an extra discount in preview calculations.
    discount: discount + deductions,
    calculation_type: 'from-total',
    tax_type: withoutTaxes ? 'individual' : taxMode,
    tax_ids: withoutTaxes ? [] : (taxMode === 'individual' ? taxIds : []),
    tax_group_id: withoutTaxes ? 0 : (taxMode === 'group' ? taxGroupId : 0),
    local_taxes: localTaxes,
  };
};

export const buildExpenseFeeStoreRequestData = (payload = {}) => {
  const input = payload && typeof payload === 'object' ? { ...payload } : {};
  const fileKeys = [
    'invoice_pdf_file',
    'invoice_xml_file',
    'voucher_file',
    'purchase_order_file',
    'additional_file',
  ];

  const hasFile = fileKeys.some((key) => input[key] instanceof File);
  if (!hasFile) {
    return input;
  }

  const formData = new FormData();

  Object.entries(input).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }

    if (value instanceof File) {
      formData.append(key, value);
      return;
    }

    if (Array.isArray(value) || (typeof value === 'object' && !(value instanceof Date))) {
      formData.append(key, JSON.stringify(value));
      return;
    }

    formData.append(key, String(value));
  });

  return formData;
};
